import { useMemo } from "react";
import { match } from "ts-pattern";
import {
  formatNodeInput,
  formatPythonInput,
  indent,
} from "../../code-snippets";
import type { AccessToken, CogInputSchema, Version } from "../../types";
import {
  ImportAndConfigureClient,
  InstallDependencies,
  RunWithContext,
  SetAuthToken,
  runWithHighlightedLanguageMap,
} from "../api-playground/run-with";
import {
  addDefaultExampleType,
  cleanInputForSubmission,
  processInputForSubmission,
} from "../api-playground/util";
import CodeBlock from "../code-block";
import type { TrainingAction } from "./shared";

export function TrainWith({
  context,
  input,
  token,
  version,
  schema,
  action,
  owner,
  requiredInputs,
}: {
  action: "update" | "create";
  context: RunWithContext.NodeJS | RunWithContext.Python;
  input: Record<string, any>;
  token?: AccessToken | null;
  version: Version;
  schema: CogInputSchema["properties"];
  owner: string;
  requiredInputs?: string[];
}) {
  const showAuthToken = token !== undefined;

  return (
    <div className="space-y-lh">
      <InstallDependencies context={context} model={version._extras.model} />
      {showAuthToken && <SetAuthToken context={context} token={token} />}
      <ImportAndConfigureClient
        context={context}
        model={version._extras.model}
      />
      <p>
        Train {version._extras.model.owner}/{version._extras.model.name} using
        Replicate’s API:
      </p>
      <RunSnippet
        action={action}
        schema={schema}
        context={context}
        input={input}
        version={version}
        owner={owner}
        requiredInputs={requiredInputs}
      />
    </div>
  );
}

function RunSnippet({
  action,
  context,
  input,
  version,
  schema,
  owner,
  requiredInputs,
}: {
  action: TrainingAction;
  context: RunWithContext.Python | RunWithContext.NodeJS;
  input: Record<string, any>;
  version: Version;
  schema: CogInputSchema["properties"];
  owner: string;
  requiredInputs?: string[];
}) {
  const language = runWithHighlightedLanguageMap[context];

  const run = useMemo(
    () =>
      match(context)
        .with(RunWithContext.NodeJS, () =>
          makeNodeTrainSnippet({
            action,
            input,
            version,
            schema,
            owner,
            requiredInputs,
          })
        )
        .with(RunWithContext.Python, () =>
          makePythonTrainSnippet({
            action,
            input,
            version,
            schema,
            owner,
            requiredInputs,
          })
        )
        .exhaustive(),
    [context, input, version, schema, action, owner, requiredInputs]
  );

  return <CodeBlock language={language} textContent={run} />;
}
const destinationClarifyingComment = (action: TrainingAction) =>
  action === "create"
    ? "You need to create a model on Replicate that will be the destination for the trained version."
    : "A new version will be created on the model that you provide here.";

function makePythonTrainSnippet({
  input,
  schema,
  action,
  owner,
  version,
  requiredInputs,
}: {
  action: TrainingAction;
  version: Version;
  input: Record<string, any>;
  schema: CogInputSchema["properties"];
  owner: string;
  requiredInputs?: string[];
}): string {
  const cleanedInput = processInputForSubmission(
    cleanInputForSubmission(input, schema, {
      alwaysInclude: requiredInputs,
    }),
    schema
  );

  const inputWithDefaults = addDefaultExampleType(cleanedInput, schema);

  const formattedInput =
    Object.keys(inputWithDefaults).length > 0
      ? formatPythonInput(inputWithDefaults, schema)
      : "{\n# ...\n}";

  return `
training = replicate.trainings.create(
  # ${destinationClarifyingComment(action)}
  destination="${owner}/${input?.destination ?? "model-name"}"
  version="${version._extras.model.owner}/${version._extras.model.name}:${
    version.id
  }",
  input=${indent(formattedInput, { first: 0, inner: 4, last: 2 })},
)`;
}

function makeNodeTrainSnippet({
  action,
  input,
  schema,
  owner,
  version,
  requiredInputs,
}: {
  action: TrainingAction;
  version: Version;
  input: Record<string, any>;
  schema: CogInputSchema["properties"];
  owner: string;
  requiredInputs?: string[];
}): string {
  const cleanedInput = processInputForSubmission(
    cleanInputForSubmission(input, schema, {
      alwaysInclude: requiredInputs,
    }),
    schema
  );

  const inputWithDefaults = addDefaultExampleType(cleanedInput, schema);

  const formattedInput =
    Object.keys(inputWithDefaults).length > 0
      ? formatNodeInput(inputWithDefaults, schema)
      : "{\n// ...\n}";

  return `
const training = await replicate.trainings.create(
  "${version._extras.model.owner}",
  "${version._extras.model.name}",
  "${version.id}",
  {
    // ${destinationClarifyingComment(action)}
    destination: "${owner}/${input?.destination ?? "model-name"}",
    input: ${indent(formattedInput, { first: 0, inner: 6, last: 4 })}
  }
);
`;
}
